import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '@clerk/tanstack-react-start'
import { Button, Group, Modal, Stack, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { useMutation } from 'convex/react'
import { api } from 'convex/_generated/api'
import {
  clearAllLivingGuideUserStorageForSlug,
  clearPostClaimAnonymousOnlyKeys,
  consumeSignupIntentFromGuide,
  hasSignupIntentFromGuide,
  isLivingGuideClaimComplete,
  markLivingGuideClaimComplete,
  setSignupIntentFromGuide,
} from '../utils/living-guide-storage'
import type { Id } from 'convex/_generated/dataModel'
import type { Dispatch, SetStateAction } from 'react'
import { useProfile } from '@/hooks/convex/useProfile'

const SAVED_TOAST = 'diy-living-claim-saved'
const ERR_TOAST = 'diy-living-claim-err'
const MAX_CTA_AUTO_RETRIES = 3

interface UseLivingGuideClaimArgs {
  templateId: Id<'projectTemplates'>
  templateSlug: string
  answers: Record<string, string>
  isHydrated: boolean
  setAnswers: Dispatch<SetStateAction<Record<string, string>>>
  /** Fires after a successful upsert from claim modal or Save my plan */
  onClaimSuccess?: (payload: {
    projectSlug: string
    personalizationPath: 'customized' | 'default'
    answerCount: number
  }) => void
}

/**
 * After sign-in, migrates local guide Q&A to Convex via `upsertProjectFromDiyGuide`
 * (same data path as `createProjectFromTemplate` — see `insertNewProjectFromDiyTemplate`).
 * CTA from the guide sets session intent for silent claim; other entry points get a
 * “Continue your personalized plan?” modal (AC #2).
 */
export function useLivingGuideClaim({
  templateId,
  templateSlug,
  answers,
  isHydrated,
  setAnswers,
  onClaimSuccess,
}: UseLivingGuideClaimArgs) {
  const hasLocalAnswers = Object.keys(answers).length > 0
  const { isSignedIn, isLoaded: clerkLoaded } = useAuth()
  const { isReady: profileReady, isPending: profilePending } = useProfile()
  const upsert = useMutation(api.diy.templates.upsertProjectFromDiyGuide)
  const [claiming, setClaiming] = useState(false)
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false)
  /** Bumps to re-run the post-auth effect after a failed CTA auto-claim (intent stays until success). */
  const [ctaRetryGeneration, setCtaRetryGeneration] = useState(0)
  const ctaAutoFailCountRef = useRef(0)

  // Always read latest at claim time; answers may hydrate after the first effect run.
  const answersRef = useRef(answers)
  answersRef.current = answers

  /** One claim / alternate-modal gate per mount; reset when template or auth context changes. */
  const flowStartedRef = useRef(false)
  const dismissedRef = useRef(false)

  const runClaim = useCallback(
    async (source: 'cta' | 'modal') => {
      setClaiming(true)
      const refinementAnswers = answersRef.current
      if (Object.keys(refinementAnswers).length === 0) {
        setClaiming(false)
        if (source === 'cta') flowStartedRef.current = false
        return
      }
      if (isLivingGuideClaimComplete(templateSlug)) {
        setClaiming(false)
        if (source === 'cta') flowStartedRef.current = false
        return
      }
      try {
        const { isNew } = await upsert({
          templateId,
          refinementAnswers,
        })
        if (source === 'cta') {
          consumeSignupIntentFromGuide(templateSlug)
        }
        ctaAutoFailCountRef.current = 0
        markLivingGuideClaimComplete(templateSlug)
        clearPostClaimAnonymousOnlyKeys(templateSlug)
        closeModal()
        const keys = Object.keys(refinementAnswers)
        onClaimSuccess?.({
          projectSlug: templateSlug,
          personalizationPath: keys.length > 0 ? 'customized' : 'default',
          answerCount: keys.length,
        })
        notifications.show({
          id: SAVED_TOAST,
          title: isNew ? 'Project saved to your account' : 'Project found in your account',
          message: isNew
            ? 'Your answers were saved — your plan is ready.'
            : 'Continuing from where you left off.',
          color: 'green',
        })
      } catch (e) {
        flowStartedRef.current = false
        console.error('[useLivingGuideClaim] upsert failed', e)
        if (source === 'cta' && ctaAutoFailCountRef.current < MAX_CTA_AUTO_RETRIES) {
          ctaAutoFailCountRef.current += 1
          setCtaRetryGeneration((n) => n + 1)
        }
        notifications.show({
          id: ERR_TOAST,
          title: 'Could not save your plan',
          message: 'You can try again from “Save my plan” or refresh the page.',
          color: 'red',
        })
      } finally {
        setClaiming(false)
      }
    },
    [closeModal, onClaimSuccess, templateId, templateSlug, upsert]
  )

  const runClaimRef = useRef(runClaim)
  const openModalRef = useRef(openModal)
  runClaimRef.current = runClaim
  openModalRef.current = openModal

  useEffect(() => {
    flowStartedRef.current = false
    dismissedRef.current = false
    ctaAutoFailCountRef.current = 0
  }, [templateSlug])

  useEffect(() => {
    if (!isSignedIn) {
      flowStartedRef.current = false
    }
  }, [isSignedIn])

  useEffect(() => {
    if (!isHydrated || !clerkLoaded) return
    if (!isSignedIn) return
    if (profilePending || !profileReady) return
    if (dismissedRef.current) return
    if (flowStartedRef.current) return
    if (isLivingGuideClaimComplete(templateSlug)) return
    if (!hasLocalAnswers) return

    flowStartedRef.current = true

    if (hasSignupIntentFromGuide(templateSlug)) {
      void runClaimRef.current('cta')
      return
    }
    openModalRef.current()
  }, [
    ctaRetryGeneration,
    hasLocalAnswers,
    isHydrated,
    clerkLoaded,
    isSignedIn,
    profilePending,
    profileReady,
    templateSlug,
  ])

  const onConfirmAlternate = useCallback(() => {
    void runClaim('modal')
  }, [runClaim])

  const onDeclineAlternate = useCallback(() => {
    dismissedRef.current = true
    clearAllLivingGuideUserStorageForSlug(templateSlug)
    setAnswers({})
    closeModal()
  }, [closeModal, setAnswers, templateSlug])

  const markSignupFromGuide = useCallback(() => {
    setSignupIntentFromGuide(templateSlug)
  }, [templateSlug])

  /**
   * Signed-in "Save my plan" from header / TOC — runs the same upsert as the claim modal
   * without requiring prior sign-up intent (Story 2.13 code review).
   */
  const savePlanFromGuide = useCallback(async (): Promise<
    'profile-not-ready' | 'no-answers' | 'already-saved' | 'upsert-finished'
  > => {
    if (!clerkLoaded || profilePending || !profileReady) {
      return 'profile-not-ready'
    }
    if (Object.keys(answersRef.current).length === 0) {
      return 'no-answers'
    }
    if (isLivingGuideClaimComplete(templateSlug)) {
      return 'already-saved'
    }
    await runClaim('modal')
    return 'upsert-finished'
  }, [clerkLoaded, profilePending, profileReady, runClaim, templateSlug])

  const alternateClaimModal = (
    <Modal
      opened={modalOpened}
      onClose={onDeclineAlternate}
      title="Continue your personalized plan?"
      data-testid="living-guide-claim-modal"
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          We found answers you entered before you signed in. Save them to your account
          to keep this guide in sync on all your devices.
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onDeclineAlternate} disabled={claiming}>
            Dismiss
          </Button>
          <Button loading={claiming} onClick={onConfirmAlternate}>
            Save to my account
          </Button>
        </Group>
      </Stack>
    </Modal>
  )

  return { markSignupFromGuide, alternateClaimModal, claiming, savePlanFromGuide }
}
